/**
 * instagram-feed.js
 * Uses fixed layout defaults for slides and spacing.
 * No secrets, no dependencies.
 */

class IGFeed {
  constructor(root) {
    this.root     = root;
    this.endpoint = root.dataset.endpoint;
    this.cfg = {
      desktopSlides: 3,
      tabletSlides: 2,
      mobileSlides: 1,
      gap: 15,
      autoplay: root.dataset.autoplay === 'true',
      delay:    parseInt(root.dataset.autoplayDelay) || 4000,
      loop:     root.dataset.loop !== 'false',
    };

    this._renderSkeletons();
    this._fetch();
  }

  /* ── Data ────────────────────────────────────────────────────────────── */

  async _fetch() {
    try {
      const res = await fetch(this.endpoint, { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { data } = await res.json();
      const posts = (data || []).filter(p => p.media_url || p.thumbnail_url).slice(0, 16);
      if (!posts.length) throw new Error('No posts returned');
      this._renderCarousel(posts);
    } catch (err) {
      this.root.innerHTML = `<p class="ig-error">Instagram feed unavailable. <small>${err.message}</small></p>`;
    }
  }

  /* ── Render ──────────────────────────────────────────────────────────── */

  _renderSkeletons() {
    const { gap } = this.cfg;
    const visible = this._visibleSlides();
    const w = this._slideWidth(visible, gap);
    this.root.innerHTML = `
      <div class="ig-carousel-container">
        ${this._arrowHTML('prev', true)}
        <div class="ig-carousel-inner">
          <div class="ig-carousel-track">
            ${Array(visible).fill(0).map(() =>
              `<div class="ig-slide" style="${this._slideStyle(w, gap)}">
                <div class="ig-skeleton" style=""></div>
              </div>`
            ).join('')}
          </div>
        </div>
        ${this._arrowHTML('next', true)}
      </div>`;
  }

  _renderCarousel(posts) {
    const { gap } = this.cfg;
    const visible = this._visibleSlides();
    const w = this._slideWidth(visible, gap);

    this.root.innerHTML = `
      <div class="ig-carousel-container">
        ${this._arrowHTML('prev')}
        <div class="ig-carousel-inner">
          <div class="ig-carousel-track">
            ${posts.map((p, i) => this._slideHTML(p, i, w, gap)).join('')}
          </div>
        </div>
        ${this._arrowHTML('next')}
      </div>`;

    this.current = 0;
    this.total   = posts.length;
    this.track   = this.root.querySelector('.ig-carousel-track');
    this.btnPrev = this.root.querySelector('.ig-arrow-prev');
    this.btnNext = this.root.querySelector('.ig-arrow-next');
    this.timer   = null;

    this._setTransform(false);
    this._updateControls();
    this._bindEvents();
    if (this.cfg.autoplay) this._startAutoplay();
  }

  _slideHTML(post, i, w, gap) {
    const src = post.media_type === 'VIDEO' ? (post.thumbnail_url || post.media_url) : post.media_url;
    const alt = (post.caption || 'Instagram post').replace(/"/g, '&quot;').slice(0, 120);
    return `
      <div class="ig-slide" style="${this._slideStyle(w, gap)}" data-i="${i}">
        <a class="ig-card" href="${post.permalink}" target="_blank" rel="noopener noreferrer" aria-label="${alt}">
          <div class="ig-card-media">
            <img src="${src}" alt="${alt}" loading="lazy" decoding="async">
          </div>
          <div class="ig-card-overlay">
            <svg class="ig-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M14.5 12.375V4.583a2.152 2.152 0 0 0-2.167-2.166h-7.75a2.152 2.152 0 0 0-2.167 2.166v7.792c0 1.208.959 2.167 2.167 2.167h7.792c1.166-.042 2.125-1 2.125-2.167Zm1.833-6.125v6.708a3.432 3.432 0 0 1-3.416 3.417h-6.71c-.25 0-.375.292-.208.458.417.459 1 .75 1.708.75h5.583a4.286 4.286 0 0 0 4.292-4.291V7.708c0-.666-.291-1.291-.75-1.708-.208-.167-.5 0-.5.25Z"/>
            </svg>
          </div>
        </a>
      </div>`;
  }

  _arrowHTML(dir, disabled = false) {
    const isPrev = dir === 'prev';
    const path   = isPrev
      ? 'M8.96 1.96a1 1 0 0 1 1.497 1.32l-.083.094L5.747 8l4.627 4.626a1 1 0 0 1 .083 1.32l-.083.094a1 1 0 0 1-1.32.084l-.094-.084-5.334-5.333a1 1 0 0 1-.083-1.32l.083-.094L8.96 1.96Z'
      : 'M7.04 1.96a1 1 0 0 0-1.497 1.32l.083.094L10.253 8l-4.627 4.626a1 1 0 0 0-.083 1.32l.083.094a1 1 0 0 0 1.32.084l.094-.084 5.334-5.333a1 1 0 0 0 .083-1.32l-.083-.094L7.04 1.96Z';
    return `
      <button class="ig-arrow ig-arrow-${dir}" aria-label="${isPrev ? 'Previous' : 'Next'}"${disabled ? ' disabled' : ''}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path fill-rule="evenodd" d="${path}"/></svg>
      </button>`;
  }

  /* ── Layout helpers ──────────────────────────────────────────────────── */

  _visibleSlides() {
    const w = this.root.offsetWidth || 960;
    if (w < 750) return this.cfg.mobileSlides;
    if (w < 1024) return this.cfg.tabletSlides;
    return this.cfg.desktopSlides;
  }

  _slideWidth(visible, gap) {
    const inner = this.root.querySelector('.ig-carousel-inner');
    const w = inner ? inner.offsetWidth : (this.root.offsetWidth || 960);
    return (w - gap * (visible - 1)) / visible;
  }

  _slideStyle(w, gap) {
    return `flex:0 0 ${w}px;min-width:${w}px;margin-right:${gap}px`;
  }

  /* ── Navigation ──────────────────────────────────────────────────────── */

  _setTransform(animate = true) {
    if (!this.track) return;
    const visible = this._visibleSlides();
    const w       = this._slideWidth(visible, this.cfg.gap);

    // Keep slide widths in sync with current breakpoint so transform distance is accurate.
    this.track.querySelectorAll('.ig-slide').forEach((slide) => {
      slide.style.flex = `0 0 ${w}px`;
      slide.style.minWidth = `${w}px`;
      slide.style.marginRight = `${this.cfg.gap}px`;
    });

    const offset  = this.current * (w + this.cfg.gap);
    this.track.style.transition = animate ? 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)' : 'none';
    this.track.style.transform  = `translateX(-${offset}px)`;
  }

  _updateControls() {
    if (!this.btnPrev) return;
    const max = this.total - this._visibleSlides();
    if (this.cfg.loop) {
      this.btnPrev.disabled = false;
      this.btnNext.disabled = false;
    } else {
      this.btnPrev.disabled = this.current === 0;
      this.btnNext.disabled = this.current >= max;
    }
  }

  goTo(index, animate = true) {
    const max = this.total - this._visibleSlides();
    if (this.cfg.loop) {
      if (index < 0) index = Math.max(max, 0);
      else if (index > max) index = 0;
    } else {
      index = Math.max(0, Math.min(index, max));
    }
    this.current = index;
    this._setTransform(animate);
    this._updateControls();
  }

  /* ── Events ──────────────────────────────────────────────────────────── */

  _bindEvents() {
    this.btnPrev.addEventListener('click', () => { this._resetAutoplay(); this.goTo(this.current - 1); });
    this.btnNext.addEventListener('click', () => { this._resetAutoplay(); this.goTo(this.current + 1); });

    const inner = this.root.querySelector('.ig-carousel-inner');
    let startX = 0;

    inner.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    inner.addEventListener('touchend',   e => {
      const dx = startX - e.changedTouches[0].clientX;
      if (Math.abs(dx) > 40) { this._resetAutoplay(); this.goTo(this.current + (dx > 0 ? 1 : -1)); }
    }, { passive: true });

    let dragX = 0, dragging = false;
    inner.addEventListener('mousedown',  e => { dragX = e.clientX; dragging = true; });
    inner.addEventListener('mouseup',    e => {
      if (!dragging) return; dragging = false;
      const dx = dragX - e.clientX;
      if (Math.abs(dx) > 40) { this._resetAutoplay(); this.goTo(this.current + (dx > 0 ? 1 : -1)); }
    });
    inner.addEventListener('mouseleave', () => { dragging = false; });

    window.addEventListener('resize', () => { this._setTransform(false); this._updateControls(); });
  }

  _startAutoplay() {
    this.timer = setInterval(() => this.goTo(this.current + 1), this.cfg.delay);
  }

  _resetAutoplay() {
    if (!this.cfg.autoplay) return;
    clearInterval(this.timer);
    this._startAutoplay();
  }
}

/* ── Boot ────────────────────────────────────────────────────────────────
   Initialises every .ig-feed-root on the page (supports multiple sections).
   ────────────────────────────────────────────────────────────────────── */
document.querySelectorAll('.ig-feed-root[data-endpoint]').forEach(root => new IGFeed(root));
